// Tenantra Inc. - Waitlist activation: everything they submitted is pre-filled;
// they only set a password + the one field we didn't collect (postal code).
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, ShieldCheck, Rocket } from 'lucide-react';
import { Logo } from '@/components/Logo';
import api, { apiHelpers } from '@/services/api';
import { setTokens, setUser } from '@/services/auth';

const PROVINCES = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'], ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'],
  ['SK', 'Saskatchewan'], ['YT', 'Yukon'],
];
const inputCls = 'w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition';
const roleLabel = { owner: 'Landlord / Property Owner', property_manager: 'Property Manager' };

export default function WaitlistActivatePage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [state, setState] = useState('loading'); // loading | ready | invalid
  const [prefill, setPrefill] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [phone, setPhone] = useState('');
  const [province, setProvince] = useState('');
  const [postal, setPostal] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      try {
        const r = await api.get(`/waitlist/invite/${token}`);
        setPrefill(r.data);
        setPhone(r.data.phone || '');
        setProvince(r.data.province || '');
        setState('ready');
      } catch { setState('invalid'); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Use at least 8 characters for your password'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    const normPhone = phone.replace(/[^\d+]/g, '');
    if (!/^\+1\d{10}$/.test(normPhone)) { toast.error('Enter phone as +1 followed by 10 digits'); return; }
    if (!province) { toast.error('Please select your province'); return; }
    if (!/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(postal)) { toast.error('Enter a valid postal code'); return; }
    setBusy(true);
    try {
      const r = await api.post('/waitlist/activate', { token, password, postal_code: postal, phone: normPhone, province });
      setTokens(r.data.access_token, r.data.refresh_token);
      setUser(r.data.user);
      toast.success('Welcome to Tenantra! 🎉');
      setTimeout(() => { window.location.href = '/landlord/dashboard'; }, 900);
    } catch (err) { toast.error(apiHelpers.handleError(err)); } finally { setBusy(false); }
  };

  if (state === 'loading') {
    return <div className="min-h-screen flex items-center justify-center bg-stone-950"><Loader2 className="w-6 h-6 animate-spin text-teal-400" /></div>;
  }
  if (state === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950 px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl p-7 text-center" data-testid="activate-invalid">
          <div className="flex justify-center mb-4"><Logo size="md" /></div>
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Link expired or invalid</h1>
          <p className="text-sm text-slate-500 mb-5">This activation link isn't valid anymore. If you already activated, just sign in. Otherwise contact us and we'll send a fresh link.</p>
          <a href="/login" className="inline-flex h-10 px-5 items-center justify-center rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium">Go to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl p-7 shadow-xl" data-testid="activate-card">
        <div className="flex items-center justify-between mb-5">
          <Logo size="md" />
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[11px] font-semibold"><Rocket className="w-3 h-3" />We're live</span>
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-1">Claim your account, {(prefill.full_name || '').split(' ')[0]} 🎉</h1>
        <p className="text-sm text-slate-500 mb-5">Everything you gave us on the waitlist is already saved. Just set a password and confirm a couple of details.</p>

        {/* Saved-from-waitlist summary (read-only, so they never re-type it) */}
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-5 text-sm" data-testid="activate-prefill">
          <div className="flex items-center gap-2 text-teal-700 text-xs font-semibold mb-2"><CheckCircle2 className="w-3.5 h-3.5" />FROM YOUR WAITLIST SIGN-UP</div>
          <Row label="Name" value={prefill.full_name} />
          <Row label="Email" value={prefill.email} />
          <Row label="Role" value={roleLabel[prefill.role_type] || prefill.role_type} />
        </div>

        <form onSubmit={submit} className="space-y-3.5">
          <label className="block">
            <span className="text-xs font-medium text-slate-600 mb-1 block">Create a password <span className="text-teal-600">*</span></span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" className={inputCls} data-testid="activate-password" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600 mb-1 block">Confirm password <span className="text-teal-600">*</span></span>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" autoComplete="new-password" className={inputCls} data-testid="activate-confirm" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600 mb-1 block">Phone <span className="text-teal-600">*</span></span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 416 555 0123" autoComplete="tel" className={inputCls} data-testid="activate-phone" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600 mb-1 block">Province <span className="text-teal-600">*</span></span>
              <select value={province} onChange={(e) => setProvince(e.target.value)} className={inputCls} data-testid="activate-province">
                <option value="">Select…</option>
                {PROVINCES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600 mb-1 block">Postal code <span className="text-teal-600">*</span></span>
              <input value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="M5V 2T6" className={inputCls} data-testid="activate-postal" />
            </label>
          </div>
          <button disabled={busy} className="w-full h-11 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors" data-testid="activate-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}<ShieldCheck className="w-4 h-4" />Activate my account
          </button>
        </form>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-700 font-medium text-right">{value || '—'}</span>
    </div>
  );
}
